"use client";

import { useMemo } from "react";
import { cn } from "@/shared/lib/utils";

export interface MeteorsProps {
  /** Количество метеоров (по умолчанию 10 — сбалансировано по нагрузке и виду) */
  number?: number;
  /** Длина хвоста в px (по умолчанию зависит от length) */
  length?: "short" | "medium" | "long" | number;
  /** Скорость: множитель длительности анимации (меньше = быстрее) */
  speed?: number;
  /** Яркость/видимость: от 0 до 1 */
  opacity?: number;
  /** Цветовая схема */
  variant?: "default" | "subtle" | "bright";
  className?: string;
}

const METEOR_OFFSETS: [number, number][] = [
  [10, 20],
  [15, 80],
  [25, 40],
  [30, 60],
  [45, 15],
  [50, 90],
  [60, 35],
  [70, 55],
  [80, 10],
  [85, 70],
  [90, 45],
  [95, 25],
  [5, 50],
  [40, 75],
  [55, 5],
  [65, 95],
  [75, 30],
  [20, 65],
  [35, 85],
  [0, 40],
];

const LENGTH_PX = {
  short: 80,
  medium: 128,
  long: 192,
} as const;

const TAIL_COLORS = {
  default: { head: "rgba(255,255,255,0.9)", mid: "rgba(255,255,255,0.3)", end: "transparent" },
  subtle: { head: "rgba(255,255,255,0.7)", mid: "rgba(255,255,255,0.2)", end: "transparent" },
  bright: { head: "rgba(255,255,255,1)", mid: "rgba(255,255,255,0.5)", end: "transparent" },
} as const;

const STAR_SIZE = {
  default: 4,
  subtle: 3,
  bright: 5,
} as const;

function getMeteorStyle(
  index: number,
  speed: number,
  opacity: number,
) {
  const [left, top] = METEOR_OFFSETS[index % METEOR_OFFSETS.length] ?? ([0, 0] as [number, number]);
  const delay = (index * 0.15) % 5;
  const baseDuration = 4 + (index % 3);
  const duration = baseDuration / speed;
  return {
    left: `${left}%`,
    top: `${top}%`,
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
    opacity,
  };
}

export function Meteors({
  number = 10,
  length = "short",
  speed = 1,
  opacity = 0.9,
  variant = "subtle",
  className,
}: MeteorsProps) {
  const tailLength = typeof length === "number" ? length : LENGTH_PX[length];
  const colors = TAIL_COLORS[variant];
  const starSize = STAR_SIZE[variant];

  const meteors = useMemo(
    () =>
      Array.from({ length: number }, (_, i) => ({
        id: i,
        style: getMeteorStyle(i, speed, opacity),
      })),
    [number, speed, opacity],
  );

  return (
    <div
      className={cn("absolute inset-0 overflow-hidden", className)}
      aria-hidden
    >
      {meteors.map(({ id, style }) => (
        <span
          key={id}
          className="absolute animate-meteor-effect"
          style={{
            ...style,
            width: `${tailLength}px`,
            height: "2px",
          }}
        >
          {/* Четырёхконечная звезда — голова метеора */}
          <svg
            className="absolute left-0 -translate-y-1/2 top-1/2"
            width={starSize * 2 + 2}
            height={starSize * 2 + 2}
            viewBox="0 0 20 20"
            style={{
              filter: `drop-shadow(0 0 ${starSize}px ${colors.head})`,
            }}
          >
            <path
              d="M10 0 L12 8 L20 10 L12 12 L10 20 L8 12 L0 10 L8 8 Z"
              fill={colors.head}
            />
          </svg>
          {/* Хвост — сужающийся градиент */}
          <span
            className="absolute inset-0 rounded-full"
            style={{
              background: `linear-gradient(to left, ${colors.end}, ${colors.mid} 30%, ${colors.head})`,
              maskImage: "linear-gradient(to left, transparent, black 10%, black)",
              WebkitMaskImage: "linear-gradient(to left, transparent, black 10%, black)",
              filter: `blur(0.5px) drop-shadow(0 0 3px ${colors.mid})`,
              clipPath: "polygon(0% 0%, 100% 30%, 100% 70%, 0% 100%)",
            }}
          />
          {/* Внутреннее свечение — яркая сердцевина хвоста */}
          <span
            className="absolute top-1/2 left-0 -translate-y-1/2 rounded-full"
            style={{
              width: `${tailLength * 0.6}px`,
              height: "1px",
              background: `linear-gradient(to left, transparent, ${colors.head})`,
              filter: `blur(0.3px)`,
            }}
          />
        </span>
      ))}
    </div>
  );
}
